import { Center, Loader } from '@mantine/core';
import {
  Navigate,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';

import { LoginPage } from '../features/auth/LoginPage';
import { NoAccessPage } from '../features/auth/NoAccessPage';
import { ProfilePage } from '../features/auth/ProfilePage';
import { AircraftAlertsTab } from '../features/aircraft/AircraftAlertsTab';
import { AircraftCountersTab } from '../features/aircraft/AircraftCountersTab';
import { AircraftDetailLayout } from '../features/aircraft/AircraftDetailLayout';
import { AircraftDocumentsTab } from '../features/aircraft/AircraftDocumentsTab';
import { AircraftListPage } from '../features/aircraft/AircraftListPage';
import { AircraftLogbookTab } from '../features/aircraft/AircraftLogbookTab';
import { AircraftMaintenanceTab } from '../features/aircraft/AircraftMaintenanceTab';
import { AircraftOverviewTab } from '../features/aircraft/AircraftOverviewTab';
import { AlertsPage } from '../features/alerts/AlertsPage';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { LogbookPage } from '../features/logbook/LogbookPage';
import { MaintenancePage } from '../features/maintenance/MaintenancePage';
import { OrganisationPage } from '../features/organisation/OrganisationPage';
import { SettingsPage } from '../features/settings/SettingsPage';
import { StaffDetailPage } from '../features/staff/StaffDetailPage';
import { StaffListPage } from '../features/staff/StaffListPage';
import { AppLayout } from './AppLayout';
import { useSession } from './session';

/**
 * Decides between the sign-in screen and the application.
 *
 * Routing cannot start until the session is known, because every path below `/$org` depends
 * on which clubs the user may see.
 */
function RootRoute() {
  const { isLoading, isAuthenticated, hasNoAccess } = useSession();

  if (isLoading) {
    return (
      <Center mih="100vh">
        <Loader />
      </Center>
    );
  }
  if (!isAuthenticated) return <LoginPage />;
  if (hasNoAccess) return <NoAccessPage />;
  return <Outlet />;
}

const rootRoute = createRootRoute({ component: RootRoute });

/** `/` sends the user to their own club, whichever that is. */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: RedirectToOrganisation,
});

function RedirectToOrganisation() {
  const { organisation } = useSession();
  if (!organisation) return <NoAccessPage />;
  return <Navigate to="/$org/dashboard" params={{ org: organisation.slug }} replace />;
}

const orgRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/$org',
  component: AppLayout,
});

const orgIndexRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/$org/dashboard', params, replace: true });
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'dashboard',
  component: DashboardPage,
});

const organisationRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'organisation',
  component: OrganisationPage,
  validateSearch: (search: Record<string, unknown>) => ({
    tab: (search.tab as string) || undefined,
  }),
});

const staffRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'staff',
  component: StaffListPage,
});

const staffDetailRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'staff/$personId',
  component: StaffDetailPage,
  validateSearch: (search: Record<string, unknown>) => ({
    tab: (search.tab as string) || undefined,
  }),
});

const aircraftRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'aircraft',
  component: AircraftListPage,
});

/** The aircraft tabs are real routes, so a maintainer can bookmark one aircraft's due list. */
const aircraftDetailRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'aircraft/$registration',
  component: AircraftDetailLayout,
});

const aircraftOverviewRoute = createRoute({
  getParentRoute: () => aircraftDetailRoute,
  path: '/',
  component: AircraftOverviewTab,
});

const aircraftLogbookRoute = createRoute({
  getParentRoute: () => aircraftDetailRoute,
  path: 'logbook',
  component: AircraftLogbookTab,
});

const aircraftMaintenanceRoute = createRoute({
  getParentRoute: () => aircraftDetailRoute,
  path: 'maintenance',
  component: AircraftMaintenanceTab,
});

const aircraftCountersRoute = createRoute({
  getParentRoute: () => aircraftDetailRoute,
  path: 'counters',
  component: AircraftCountersTab,
});

const aircraftAlertsRoute = createRoute({
  getParentRoute: () => aircraftDetailRoute,
  path: 'alerts',
  component: AircraftAlertsTab,
});

const aircraftDocumentsRoute = createRoute({
  getParentRoute: () => aircraftDetailRoute,
  path: 'documents',
  component: AircraftDocumentsTab,
});

const logbookRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'logbook',
  component: LogbookPage,
  validateSearch: (search: Record<string, unknown>) => ({
    scope: (search.scope as string) || undefined,
    subject: search.subject ? Number(search.subject) : undefined,
  }),
});

const maintenanceRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'maintenance',
  component: MaintenancePage,
  validateSearch: (search: Record<string, unknown>) => ({
    tab: (search.tab as string) || undefined,
  }),
});

const alertsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'alerts',
  component: AlertsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    tab: (search.tab as string) || undefined,
  }),
});

const settingsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'settings',
  component: SettingsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    tab: (search.tab as string) || undefined,
  }),
});

const profileRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: 'profile',
  component: ProfilePage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  orgRoute.addChildren([
    orgIndexRoute,
    dashboardRoute,
    organisationRoute,
    staffRoute,
    staffDetailRoute,
    aircraftRoute,
    aircraftDetailRoute.addChildren([
      aircraftOverviewRoute,
      aircraftLogbookRoute,
      aircraftMaintenanceRoute,
      aircraftCountersRoute,
      aircraftAlertsRoute,
      aircraftDocumentsRoute,
    ]),
    logbookRoute,
    maintenanceRoute,
    alertsRoute,
    settingsRoute,
    profileRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
